import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEsgQuestionnaires, useQuestionnaireMethodology } from '../hooks';
import { Box } from '@mui/system';
import { FormControl, Typography } from '@mui/material';
import { StyledPanel } from 'src/components/StyledPanel';
import { IntuitiveCalendarPeriod } from 'src/views/components/IntuitiveCalendar';
import { ReportingFreq } from 'src/utils/api.interfaces';
import { periodToDate, timeConverter } from 'src/utils/timeConverter';
import { EsgQuestionnaireSimpleSchema } from '../types';
import ConsolidationQuestionPanels from './Consolidation';
import Autocomplete from 'src/components/Autocomplete';
import ExportToCsvButton from '../Dmav2/components/DmaExportButton';
import { useExportToFileMutation } from '../../common/hooks';

export default function EsgDataVisualization() {
  const {
    t,
    i18n: { t: tRoot },
  } = useTranslation(undefined, {
    keyPrefix: 'esg:dataVisualization',
  });
  // TODO methodology selector?
  const { _data: methodologies } = useQuestionnaireMethodology({
    name: 'EFRAG XBRL (2024/09)',
  });
  const methodology = methodologies?.[0];
  const { data: questionnaires } = useEsgQuestionnaires(methodology?.id);

  const [period, setPeriod] = React.useState<string>(
    timeConverter(new Date(), ReportingFreq.Y)
  );

  const [questionnaire, setQuestionnaire] =
    React.useState<EsgQuestionnaireSimpleSchema | null>(null);

  const handleQuestionnaireChange = (value: string | null) => {
    setQuestionnaire(
      questionnaires.find((questionnaire) => questionnaire.key === value) ??
        null
    );
  };

  const exportConsolidationTablesToDocx = useExportToFileMutation(
    '/web/esg/v3/consolidated-tables'
  );

  useEffect(() => {
    if (questionnaires.length > 0) {
      setQuestionnaire(questionnaires[0]);
    }
  }, [questionnaires]);

  return (
    <Box sx={{ p: '16px 20px' }}>
      <Typography variant="h1" sx={{ mb: '32px' }}>
        {t('title')}
      </Typography>
      <StyledPanel>
        <FormControl fullWidth sx={{ mb: '16px' }}>
          <Autocomplete
            label={t('selectQuestionnaireKey')}
            placeholder={t('selectQuestionnaireKey')}
            options={questionnaires.map((questionnaire) => ({
              value: questionnaire.key,
              label: questionnaire.title,
            }))}
            value={questionnaire?.key ?? null}
            onChange={handleQuestionnaireChange}
            disableClearable
          />
        </FormControl>
        <IntuitiveCalendarPeriod
          reportingPeriod={ReportingFreq.Y}
          selectedDate={periodToDate(period, ReportingFreq.Y)}
          setSelectedDate={(selDate: Date) =>
            setPeriod(timeConverter(selDate, ReportingFreq.Y))
          }
        />
        <Box display="flex" justifyContent="flex-end" mt="20px">
          <ExportToCsvButton
            handleExportToCsv={() =>
              exportConsolidationTablesToDocx.mutateAsync({
                defaultFilename: 'data.docx',
                params: {
                  period,
                  questionnaire_methodology_name: methodology?.name,
                },
              })
            }
            customText={tRoot('esg:exportDocx')}
          />
        </Box>
      </StyledPanel>
      {!!questionnaire && (
        <ConsolidationQuestionPanels
          questionnaireKey={questionnaire.key}
          period={period}
          color={questionnaire.color}
        />
      )}
    </Box>
  );
}
